// Quando chega a primeira leitura semanal de um quadro (aba "Leitura da IA").
// Espelha o backend (pontuax-backend, attendant-goal-ai/readings.ts, computeBoardWeeks):
//  - a aba lê a ÚLTIMA SEMANA FECHADA (segunda a domingo) e só conta se o quadro valia em 5 ou mais dias dela;
//  - o "dia que vale" é o dia BRT dentro de [início, fim] (fim = o menor entre end_date e, se desativado, disabled_at)
//    e com o dia da semana marcado na agenda; a semana só é lida a partir da segunda da semana do início (ou da criação).
//  - só os alvos quantidade, fidelização e volume são lidos: quadro só com Mix ou Tanque cheio devolve sempre `insufficient_data`.
// Se mudar a regra lá, mude aqui (e o teste).
import { AttendantGoalType } from '../../models/attendantGoalType';

/** READING_WEEK_MIN_ACTIVE_DAYS do backend. */
export const MIN_ACTIVE_DAYS = 5;

/** MEASURED do backend (readings.ts): os tipos de meta que a leitura sabe medir. */
const MEASURED_TYPES: string[] = [AttendantGoalType.QUANTITY, AttendantGoalType.PERCENTAGE_LOYALTY, AttendantGoalType.VOLUME];

const TZ = 'America/Sao_Paulo';
const DAY_MS = 86_400_000;
const LOOKAHEAD_WEEKS = 60;

export interface BoardPeriod {
  startDate?: string | null;
  endDate?: string | null;
  createdAt?: string | null;
  isDisabled?: boolean | null;
  disabledAt?: string | null;
  scheduleSunday?: boolean | null;
  scheduleMonday?: boolean | null;
  scheduleTuesday?: boolean | null;
  scheduleWednesday?: boolean | null;
  scheduleThursday?: boolean | null;
  scheduleFriday?: boolean | null;
  scheduleSaturday?: boolean | null;
  /** Metas do quadro. Sem o campo (registro sem `targets`), não se afirma nada sobre o tipo da meta. */
  targets?: { type?: string | null }[] | null;
}

export enum OutlookKind {
  /** A primeira leitura sai numa segunda-feira ('YYYY-MM-DD'). */
  upcoming = 'upcoming',
  /** A última semana fechada devia valer, mas faltou dado de frentista identificado: nova tentativa na segunda seguinte. */
  noData = 'noData',
  /** O quadro nunca terá leitura semanal (ver `NeverReason`). */
  never = 'never',
}

export enum NeverReason {
  /** Agenda com menos de 5 dias na semana, ou quadro já encerrado ou desativado. */
  period = 'period',
  /** Nenhuma meta do quadro é de quantidade, fidelização ou volume (ex.: só Mix ou Tanque cheio). */
  targets = 'targets',
}

export type ReadingOutlook =
  | { kind: OutlookKind.upcoming; day: string }
  | { kind: OutlookKind.noData; day: string }
  | { kind: OutlookKind.never; reason: NeverReason };

/** Dia civil de São Paulo ('YYYY-MM-DD') de um instante. */
export const brtDay = (value: string | Date): string => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(value));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const addDays = (day: string, n: number): string =>
  new Date(new Date(`${day}T00:00:00Z`).getTime() + n * DAY_MS).toISOString().slice(0, 10);

/** 0 = domingo. */
const weekdayOf = (day: string): number => new Date(`${day}T00:00:00Z`).getUTCDay();

const mondayOf = (day: string): string => addDays(day, -((weekdayOf(day) + 6) % 7));

export const readingOutlook = (board: BoardPeriod, now: Date): ReadingOutlook => {
  // Com as metas conhecidas e nenhuma medível, o backend nunca gera leitura (não adianta esperar a semana valer).
  if (Array.isArray(board.targets) && !board.targets.some((t) => !!t?.type && MEASURED_TYPES.includes(t.type))) {
    return { kind: OutlookKind.never, reason: NeverReason.targets };
  }

  const schedule = [
    board.scheduleSunday, board.scheduleMonday, board.scheduleTuesday, board.scheduleWednesday,
    board.scheduleThursday, board.scheduleFriday, board.scheduleSaturday,
  ].map(Boolean);
  const start = board.startDate ? brtDay(board.startDate) : null;
  const ends = [board.endDate, board.isDisabled ? board.disabledAt : null].filter((d): d is string => !!d).map(brtDay).sort();
  const end = ends.length ? ends[0] : null;

  const isActiveDay = (day: string): boolean => {
    if ((start && day < start) || (end && day > end)) return false;
    return schedule[weekdayOf(day)];
  };
  const activeDaysIn = (weekStart: string): number => [0, 1, 2, 3, 4, 5, 6].filter((i) => isActiveDay(addDays(weekStart, i))).length;

  const today = brtDay(now);
  const anchorWeek = mondayOf(start ?? (board.createdAt ? brtDay(board.createdAt) : today));
  const thisMonday = mondayOf(today);
  const lastClosed = addDays(thisMonday, -7);

  // A última semana fechada já valeria e mesmo assim a leitura veio sem dados: a próxima tentativa é a segunda que vem.
  if (lastClosed >= anchorWeek && activeDaysIn(lastClosed) >= MIN_ACTIVE_DAYS) return { kind: OutlookKind.noData, day: addDays(thisMonday, 7) };

  // A semana corrente ainda não fechou: se ela valer, a leitura sai na segunda seguinte.
  for (let i = 0; i < LOOKAHEAD_WEEKS; i++) {
    const week = addDays(thisMonday, 7 * i);
    if (week >= anchorWeek && activeDaysIn(week) >= MIN_ACTIVE_DAYS) return { kind: OutlookKind.upcoming, day: addDays(week, 7) };
  }
  return { kind: OutlookKind.never, reason: NeverReason.period };
};

/** 'YYYY-MM-DD' → 'dd/mm'. */
export const dayMonth = (day: string): string => {
  const [, m, d] = day.split('-');
  return `${d}/${m}`;
};
